'use client';

import React from 'react';

import styled from 'styled-components';
import AntdAutoComplete from 'antd/es/auto-complete';

import CustomIcon, { SeturIcon } from '@components/utils/CustomIcon';
import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';

type AutoCompletePlacement = NonNullable<
  React.ComponentProps<typeof AntdAutoComplete>['placement']
>;

type AutoCompleteBuiltinPlacements = NonNullable<
  React.ComponentProps<typeof AntdAutoComplete>['builtinPlacements']
>;

const DEFAULT_POPUP_PLACEMENT: AutoCompletePlacement = 'bottomLeft';

const BOTTOM_LOCKED_POPUP_PLACEMENTS: AutoCompleteBuiltinPlacements = {
  bottomLeft: {
    points: ['tl', 'bl'],
    offset: [0, 4],
    overflow: {
      adjustX: true,
      adjustY: false,
    },
  },
  bottomRight: {
    points: ['tr', 'br'],
    offset: [0, 4],
    overflow: {
      adjustX: true,
      adjustY: false,
    },
  },
  topLeft: {
    points: ['bl', 'tl'],
    offset: [0, -4],
    overflow: {
      adjustX: true,
      adjustY: false,
    },
  },
  topRight: {
    points: ['br', 'tr'],
    offset: [0, -4],
    overflow: {
      adjustX: true,
      adjustY: false,
    },
  },
};

const AntAutoComplete = styled(AntdAutoComplete)<{
  $isFocus?: boolean;
  $isError?: boolean;
}>`
  &&& {
    width: 100%;
    height: auto;

    .setur-select-selection-search-input {
      color: ${({ theme }) => theme.palette.grey1100} !important;
      font-size: 14px;
      font-weight: 500;
      line-height: 24px;
    }

    .setur-select-selector {
      height: 56px !important;
      background-color: ${({ $isFocus }) =>
        $isFocus ? '' : '#fff'} !important;
      border-radius: 4px;
      border: 1px solid ${({ theme }) => theme.palette.inputBorderColor} !important;
      box-shadow: none !important;
    }

    .setur-select-dropdown {
      border-radius: 12px;
      background: ${({ theme }) => theme.palette.white};
      box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.16);
      padding: 0px;
      min-width: 350px !important;

      @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
        min-width: 100% !important;
      }
    }

    .setur-select-item-option {
      display: flex;
      align-items: center;
      padding: 8px 24px;
      min-height: 56px;

      &-active {
        background-color: ${({ theme }) => theme.palette.grey100};
      }

      &.pastSearchesTitleWrapper {
        background-color: transparent;
        padding: 24px 24px 16px;
        cursor: default;
      }
    }
  }
`;

export const Label = styled.div<{ $hasIcon?: boolean; $hasValue?: boolean }>`
  color: ${({ theme, $hasValue }) =>
    $hasValue ? theme.palette.grey1100 : theme.palette.grey800};
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  position: absolute;
  top: 16px;
  left: ${({ $hasIcon }) => ($hasIcon ? '42px' : '16px')};
  z-index: 1;
  pointer-events: none;
`;

export const IconWrapper = styled.div`
  font-size: 16px;
  position: absolute;
  top: 16px;
  left: 12px;
  z-index: 1;
  pointer-events: none;
`;

const Wrapper = styled.div<{ $editable?: boolean }>`
  pointer-events: ${({ $editable }) => ($editable ? 'auto' : 'none')};
  .setur-select-selection-search {
    padding-left: 28px;
  }
`;

interface AutoCompleteProps {
  onSearch: (val: string) => void;
  onFocus?: () => void;
  onBlur?: React.FocusEventHandler<HTMLElement> | undefined;
  onClear?: () => void;
  isFocus?: boolean;
  isError?: boolean;
  value: string;
  dropdownStyle?: React.CSSProperties;
  children?: React.ReactElement | null;
  allowClear?: boolean;
  icon?: SeturIcon;
  isStrokeIcon?: boolean;
  label?: string;
  editable?: boolean;
}

const AutoComplete = ({
  onSearch,
  onFocus,
  onBlur,
  onClear,
  isFocus,
  isError,
  value,
  dropdownStyle,
  children,
  allowClear,
  icon,
  isStrokeIcon = false,
  label,
  editable = true,
}: AutoCompleteProps) => {
  return (
    <LocationAnimationErrorBox
      hasError={(!isFocus && isError) || false}
      borderRadius='4px'
    >
      <Wrapper $editable={editable}>
        {icon ? (
          <IconWrapper>
            <CustomIcon
              icon={icon}
              fontSize={24}
              {...(isStrokeIcon
                ? { stroke: 'grey40' }
                : {
                    paletteColor: 'grey40',
                  })}
            />
          </IconWrapper>
        ) : null}
        {value ? null : (
          <Label $hasValue={Boolean(value)} $hasIcon={Boolean(icon)}>
            {label}
          </Label>
        )}

        <AntAutoComplete
          allowClear={allowClear}
          onSearch={onSearch}
          onFocus={onFocus}
          onBlur={onBlur}
          onClear={onClear}
          $isFocus={isFocus}
          $isError={isError}
          value={value}
          placement={DEFAULT_POPUP_PLACEMENT}
          builtinPlacements={BOTTOM_LOCKED_POPUP_PLACEMENTS}
          styles={{ popup: { root: dropdownStyle } }}
          getPopupContainer={(triggerNode) =>
            triggerNode?.parentNode || document?.body
          }
        >
          {children}
        </AntAutoComplete>
      </Wrapper>
    </LocationAnimationErrorBox>
  );
};

export const Option = AntAutoComplete.Option;

export default AutoComplete;
